import React, { memo } from 'react';
import ActionList from '@components/ActionsMenu/ActionList';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import CopyModelToProject from '@components/Dialogs/CopyModelToProject';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import useToggle from '@hooks/useToggle';
import { Popover } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { MoreIcon } from '@utils/iconsDefs';
import { AdditionalSettingsStyled } from '@views/Project/components/AdditionalSettings/AdditionalSettings.styled';
import useAdditionalMenuItems from '@views/Project/components/AdditionalSettings/useAdditionalMenuItems';
import useCopyModel from '@views/Project/hooks/useCopyModel';
import {
  ExportAsImageModal,
  ExportAssetReportModal,
  ExportLayersReportModal,
} from '@views/ReportsModel';
import { ExportNodesReportModal } from '@views/ReportsModel/components/ExportNodesReportModal';
import { ExportRiskRegisterReportModal } from '@views/ReportsModel/components/ExportRiskRegisterReportModal';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

export const AdditionalSettings = memo(() => {
  const {
    isVersionsShown,
    toolbox: { mode },
  } = useProject();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'project-additional-settings',
  });

  const { handleCopyModel } = useCopyModel();

  const [isExportModelImageOpen, exportModelImageControls] = useToggle();
  const [isExportLayersOpen, exportLayersControls] = useToggle();
  const [isExportNodesOpen, exportNodesControls] = useToggle();
  const [isExportAssetOpen, exportAssetControls] = useToggle();
  const [isExportRegisterOpen, exportRegisterControls] = useToggle();
  const [isCopyToProjectOpen, copyToProjectControls] = useToggle();
  const [isReplaceOpen, replaceControls, replaceProject] =
    useToggle<ISelectOption>();

  const items = useAdditionalMenuItems({
    onOpenExportAsImage: exportModelImageControls.on,
    onOpenExportLayersReport: exportLayersControls.on,
    onOpenExportFocusOnReport: exportNodesControls.on,
    onOpenExportRiskRegisterReport: exportRegisterControls.on,
    onOpenExportAssetReport: exportAssetControls.on,
    onCopyToProject: copyToProjectControls.on,
  });

  const isAdditionalSettingsDisabled = isVersionsShown;

  return (
    <>
      <ConfirmDialog
        open={isReplaceOpen}
        onClose={replaceControls.off}
        title="Replace existing model?"
        text={`Copying this model will overwrite the existing model in ${replaceProject?.label}. \nThis action cannot be undone.`}
        onConfirm={() => handleCopyModel(replaceProject, replaceControls.off)}
      />

      <CopyModelToProject
        open={isCopyToProjectOpen}
        onClose={copyToProjectControls.off}
        type="model"
        onConfirm={(project: ISelectOption) => {
          replaceControls.on(project);
          copyToProjectControls.off();
        }}
      />

      {mode && isExportModelImageOpen && (
        <ExportAsImageModal
          open={isExportModelImageOpen}
          onClose={exportModelImageControls.off}
        />
      )}

      {isExportLayersOpen && (
        <ExportLayersReportModal
          open={isExportLayersOpen}
          onClose={exportLayersControls.off}
        />
      )}

      {isExportNodesOpen && (
        <ExportNodesReportModal
          open={isExportNodesOpen}
          onClose={exportNodesControls.off}
        />
      )}

      {isExportAssetOpen && (
        <ExportAssetReportModal
          open={isExportAssetOpen}
          onClose={exportAssetControls.off}
        />
      )}

      {isExportRegisterOpen && (
        <ExportRiskRegisterReportModal
          open={isExportRegisterOpen}
          onClose={exportRegisterControls.off}
        />
      )}

      <AdditionalSettingsStyled
        $isActive={popupState.isOpen}
        disabled={isAdditionalSettingsDisabled}
        $isDisabled={isAdditionalSettingsDisabled}
        {...bindTrigger(popupState)}
      >
        <MoreIcon
          fill={themePalette.grey[isAdditionalSettingsDisabled ? 700 : 1000]}
        />
      </AdditionalSettingsStyled>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{ paper: { sx: { width: 220, mt: 3 } } }}
      >
        <ActionList items={items} onClose={popupState.close} />
      </Popover>
    </>
  );
});
